import { FormikErrors, useFormik } from 'formik';
import { TFunction } from 'i18next';
import React, { FC, useEffect, useState, useRef, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { useTheme } from 'styled-components';
import * as Yup from 'yup';

import {
    CREATE_ORDER_SIDE,
    DEPTH,
    ORDER_BY,
    ORDER_SIDE,
    ORDER_TYPE,
    PRICE_TYPE,
    SLIPPAGE,
    TIME_IN_FORCE,
    MAX_INPUT,
    POST_ONLY_TIME,
    CALCULATOR_TYPE,
    POSITION_SIDE,
    BonusTypeEnum,
    BonusStatusEnum,
    DEFAULT_ACCOUNT_INDEX,
    UserVerificationStatusEnum,
    SOCKET_SUB_TYPE,
    SOCKET_TOPICS,
} from 'src/common/constants';
import {
    changeScrollBehavior,
    currencyFormatter,
    digits,
    isEmptyObj,
    noNan,
    roundNumber,
    setCurrentUrl,
} from 'src/common/helpers';
import {
    Input,
    Button,
    Tabs,
    Tab,
    Checkbox,
    Range,
    Select,
    SelectItem,
    Tooltip,
    Switch,
} from 'src/components/core';
import { DropDownArrow, Info, Question } from 'src/components/core/icons';
import { FlexContainer } from 'src/components/core/layout/FlexContainer';
import { ConfirmationModal } from 'src/components/features/derivative/modals/confirmationModal';
import { useAuth } from 'src/context/AuthContext';
import { useAppDispatch, useAppSelector } from 'src/hooks';
import { useKycAndFeaturesConfig } from 'src/hooks/useKycAndFeaturesConfig';
import { APP_URLS } from 'src/setup/client/app-urls';
import {
    createOrder,
    performCalculations,
    setLeverage,
} from 'src/setup/redux/slices/derivativesSlice';
import { changeFutureTradingAccount, setSettings } from 'src/setup/redux/slices/globalSlice';
import SocketContext from 'src/setup/socket/socketContext';
import {
    DerivativeAsset,
    FutureSymbol,
    FutureSymbolSettings,
    TradeSocketResponseData,
    FutureFormElements,
    FutureTradeAble,
} from 'src/types';

import { CompleteKYCModal } from '../modals/CompleteKYCModal';
import { FuturesFundTradeInstructionModal } from '../modals/FuturesFundTradeInstructionModal';
import { LeverageModal } from '../modals/levelrageModal';

const StyledForm = styled.form`
    ${({ theme: { typography, colors, isMobile } }) => `
       .tabs {
            display: flex;
            padding: 0;
            & > .tab {
                flex: 1;
                justify-content: center;
                font-size: ${typography.size.desktop.paragraph.p3};
                margin: 0px;
            }
        }
        & > .order-side {
            padding: 0px 30px;
        }
        & > .form-elements {
            padding: 18px 13px 16px 13px;
            & > * {
                margin-bottom: 18px;
            }
            & > .info-container {
                justify-content: space-between;
                & > .order-value {
                    display: ${isMobile ? 'flex' : 'block'}
                }
            }
            .cost-container {
                justify-content: space-between;
                .value-container {
                    word-break: break-word;
                    margin-left: 20px;
                    text-align: right;
                    font-size: ${typography.size.desktop.paragraph.p3};
                }
            }
        }
        .buttons-container {
            .left-button {
                margin-right: 7.5px;
                color: ${colors.brand.main};
                padding: 10px 0px;
            }
            .right-button {
                margin-left: 7.5px;
                color: ${colors.brand.main};
                padding: 10px 0px;
            }
        }
    `}
`;

const LabelText = styled.div`
    ${({ theme: { typography, colors, isMobile } }) => `
        font-size: ${typography.size.desktop.paragraph.p3};
        color:${colors.text.tertiary};
        margin-right: ${isMobile ? '8px' : '0px'};
        white-space: nowrap;
    `}
`;

const ValueText = styled.span`
    ${({ theme: { typography } }) => `
        font-size: ${typography.size.desktop.paragraph.p3};
        word-break: break-word;
        white-space: nowrap;
    `}
`;

const Separator = styled.span`
    ${({ theme: { typography, colors } }) => `
        font-size: ${typography.size.desktop.paragraph.p3};
        color: ${colors.text.tertiary};
        white-space: nowrap;
    `}
`;

const InputRightLabel = styled.span`
    ${({ theme: { typography, colors } }) => `
        font-size: ${typography.size.desktop.paragraph.p3};
        color: ${colors.text.secondary};
        margin-right: 10px;
        white-space: nowrap;
    `}
`;

const LeverageContainer = styled(FlexContainer)`
    ${({ theme: { typography, colors } }) => `
        width: 100%;
        border: 1px solid ${colors.border.light};
        border-radius: 4px;
        font-size: ${typography.size.desktop.paragraph.p3};
    `}
`;

const LeverageTypeLabel = styled.span`
    ${({ theme: { colors } }) => `
        white-space: nowrap;
        color: ${colors.text.primary};
        padding: 12px 10px;
        border-right: 1px solid ${colors.border.light};
        white-space: nowrap;
    `}
`;

const LeverageValueText = styled.span<{ color: string }>`
    ${({ color }) => `
        white-space: nowrap;
        color: ${color};
        white-space: nowrap;
    `}
`;

const QuantityContainer = styled(FlexContainer)`
    width: max-content;
    word-break: break-word;
    cursor: pointer;
`;

const StyledLabel = styled.span<{ color: string }>`
    ${({ theme: { typography }, color }) => `
        font-size: ${typography.size.desktop.paragraph.p3};
        color: ${color};
    `}
`;

const initialOrderCost = {
    long: 0,
    short: 0,
};

const initialOrderValue = {
    long: 0,
    short: 0,
};

export const initialValues = {
    orderSide: ORDER_SIDE.OPEN,
    orderType: ORDER_TYPE.MARKET,
    priceType: PRICE_TYPE.MARKET,
    price: '',
    triggerPrice: 0,
    amount: 0,
    quantity: '',
    quantityLong: 0,
    quantityShort: 0,
    leverage: `0`,
    postOnly: false,
    timeInForce: TIME_IN_FORCE.GTC,
    percentage: 0,
    orderBy: ORDER_BY.COST,
};

const validationSchema = (maxCloseAble: number, t: TFunction<'translation', undefined>) => {
    return Yup.object().shape({
        orderSide: Yup.string().required(),
        orderType: Yup.string().required(),
        priceType: Yup.string().required(),
        price: Yup.mixed()
            .when('priceType', {
                is: PRICE_TYPE.MARKET,
                then: Yup.string(),
                otherwise: Yup.number().moreThan(
                    0,
                    t('form_validation.greater', { field: t('order_price'), length: '0' }),
                ),
            })
            .required(t('form_validation.field_required', { field: t('order_price') })),
        triggerPrice: Yup.number().when('orderType', {
            is: ORDER_TYPE.CONDITIONAL,
            then: Yup.number()
                .moreThan(
                    0,
                    t('form_validation.greater', { field: t('trigger_price'), length: '0' }),
                )
                .required(t('form_validation.field_required', { field: t('trigger_price') })),
            otherwise: Yup.number(),
        }),
        amount: Yup.number().when('orderBy', {
            is: ORDER_BY.COST,
            then: Yup.number()
                .moreThan(0, t('form_validation.greater', { field: t('amount'), length: '0' }))
                .required(t('form_validation.field_required', { field: t('amount') })),
            otherwise: Yup.number(),
        }),
        quantity: Yup.number().when('orderBy', {
            is: ORDER_BY.QUANTITY,
            then: Yup.number()
                .when('orderSide', {
                    is: ORDER_SIDE.CLOSE,
                    then: Yup.number().max(
                        maxCloseAble,
                        maxCloseAble > 0
                            ? t('form_validation.cannot_greater', {
                                  field: t('quantity'),
                                  length: maxCloseAble,
                              })
                            : t('form_validation.open.position'),
                    ),
                    otherwise: Yup.number(),
                })
                .moreThan(0, t('form_validation.greater', { field: t('quantity'), length: '0' }))
                .required(t('form_validation.field_required', { field: t('quantity') })),
            otherwise: Yup.number(),
        }),
        postOnly: Yup.bool().required(),
        timeInForce: Yup.string().required(),
        percentage: Yup.number().required(),
        orderBy: Yup.string().required(),
    });
};

const Form: FC = () => {
    const { colors, isMobile } = useTheme();
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { reSubscribeTopic } = useContext(SocketContext);
    const { allowTrading } = useKycAndFeaturesConfig();

    const [isOrderConfirmationOpen, setIsOrderConfirmationOpen] = useState(false);
    const [isDualQuantityShow, setIsDualQuantityShow] = useState(false);
    const [isLeverageModalOpen, setIsLeverageModalOpen] = useState(false);
    const [isFuturesFundTradeInstructionModalOpen, setIsFuturesFundTradeInstructionModalOpen] =
        useState(false);
    const [isShowKYCModalOpen, setIsShowKYCModalOpen] = useState(false);
    const [orderConfirmationCheckLower, setOrderConfirmationCheckLower] = useState(false);
    const [orderConfirmationCheckHigher, setOrderConfirmationCheckHigher] = useState(false);
    const [orderConfirmationCheckTrigger, setOrderConfirmationCheckTrigger] = useState(false);
    const [tokenInfo, setTokenInfo] = useState({} as TradeSocketResponseData);
    const [orderValue, setOrderValue] = useState(initialOrderValue);
    const [orderCost, setOrderCost] = useState(initialOrderCost);
    const [liquidationPrice, setLiquidationPrice] = useState('0');
    const [shouldSetPrice, setShouldSetPrice] = useState(true);
    const [totalFee, setTotalFee] = useState(0);

    const orderButtonRef = useRef('');

    const { isAuthenticated } = useAuth();

    const tokenInfoData = useAppSelector((state) => state.socket.slowBroker);
    const { userAccountLevelInfo, userBonus, userVerifyInfo } = useAppSelector(
        (state) => state.user,
    );
    const {
        currentSymbol,
        createOrderPrice,
        tradeable,
        leverage: { leverageLong, leverageShort },
        loading: { getLeverage: getLeverageLoading },
    } = useAppSelector((state) => state.derivatives);
    const createOrderLoading = useAppSelector((state) => state.derivatives.loading.createOrder);
    const {
        baseTokenFutures,
        quoteTokenName,
        symbolId,
        isReverse,
        secondLevelUnderlyingName,
        minPricePrecision,
        minTradeQuantity,
        exchangeId,
    }: FutureSymbol = useAppSelector((state) => state.derivatives.currentSymbol);
    //order setting
    const currentSymbolSettings: FutureSymbolSettings = useAppSelector(
        (state) => state.derivatives.symbolSettings,
    );
    const {
        orderSetting: { isConfirm },
    } = currentSymbolSettings;
    //assets
    const currentAsset: DerivativeAsset = useAppSelector((state) => state.derivatives.assets);

    const { userCurrentFutureTradeAccountIndex, userWallets, commonConfig } = useAppSelector(
        (state) => state.global,
    );

    const isFutureBonusAccount = !!commonConfig?.isFutureBonusAccount;

    const maxTradeQuantityDecimals = DEPTH[minTradeQuantity];
    const maxPriceDecimals = DEPTH[minPricePrecision];
    const maxMarginDecimals = DEPTH[baseTokenFutures?.marginPrecision];
    const symbolTradeableInfo: FutureTradeAble | undefined = tradeable.find(
        (item) => currentSymbol.symbolId === item.tokenId,
    );

    const {
        values,
        setFieldValue,
        setValues,
        errors,
        handleBlur,
        setErrors,
        validateForm,
        handleSubmit,
    } = useFormik({
        initialValues: initialValues,
        validationSchema: validationSchema(
            Math.max(
                Number(symbolTradeableInfo?.longAvailable),
                Number(symbolTradeableInfo?.shortAvailable),
            ),
            t,
        ),
        validateOnMount: false,
        validateOnBlur: false,
        validateOnChange: false,
        onSubmit: (values) => {
            const order = {
                side: `${orderButtonRef.current}_${values.orderSide}`,
                type: values.orderType === ORDER_TYPE.MARKET ? ORDER_TYPE.LIMIT : values.orderType,
                ...(values.priceType === PRICE_TYPE.LIMIT && { price: values.price }),
                price_type: values.priceType,
                trigger_price: values.triggerPrice,
                // leverage: values.leverage,
                quantity: values.quantity,
                symbol_id: symbolId,
                client_order_id: new Date().getTime(),
                exchange_id: exchangeId,
                order_side: orderButtonRef.current,
                time_in_force: values.postOnly ? POST_ONLY_TIME : values.timeInForce,
                account_index: userCurrentFutureTradeAccountIndex,
            };
            dispatch(createOrder(order));
        },
    });

    let updatedValues: FutureFormElements = values;
    let updatedErrors: FormikErrors<FutureFormElements> = errors;

    // regular expression to validate quantity
    const QuantityRegEx = new RegExp(
        `^[0-9]*${
            maxTradeQuantityDecimals > 0
                ? // eslint-disable-next-line prefer-template
                  '(\\.[0-9]{0,' + maxTradeQuantityDecimals + '})?$'
                : '?$'
        }`,
    );

    // regular expression to validate price
    const PriceRegEx = new RegExp(
        `^[0-9]*${
            maxPriceDecimals > 0
                ? // eslint-disable-next-line prefer-template
                  '(\\.[0-9]{0,' + maxPriceDecimals + '})?$'
                : '?$'
        }`,
    );

    // regular expression to validate amount
    const AmountRegEx = new RegExp(
        `^[0-9]*${
            maxMarginDecimals > 0
                ? // eslint-disable-next-line prefer-template
                  '(\\.[0-9]{0,' + maxMarginDecimals + '})?$'
                : '?$'
        }`,
    );

    const hideDualQuantity = (e: any) => {
        e.target.parentElement.parentElement.previousElementSibling.focus();
        setIsDualQuantityShow(false);
        updatedValues.quantity = '';
        updatedValues.amount = 0;
    };

    const toggleOrderConfirmation = () => {
        changeScrollBehavior(!isOrderConfirmationOpen);
        setIsOrderConfirmationOpen(!isOrderConfirmationOpen);
    };

    const getPrice = ({ price, priceType }: FutureFormElements) => {
        let p = 0;
        if (priceType === PRICE_TYPE.LIMIT) {
            p = Number(price);
        } else if (priceType === PRICE_TYPE.MARKET) {
            p = Number(tokenInfo.c);
        }
        return p;
    };

    const calculateFeeRate = () => {
        const { fees, userLevelInfo } = userAccountLevelInfo;

        if (!fees) return 0;

        const isTaker = updatedValues.orderType === ORDER_TYPE.MARKET;
        const isBuyOrder = orderButtonRef.current === CREATE_ORDER_SIDE.BUY;

        let fee, discount;

        if (isBuyOrder) {
            fee = isTaker ? fees.contractBuyTakerFee : fees.contractBuyMakerFee;
            discount = isTaker
                ? userLevelInfo.contractBuyTakerDiscount
                : userLevelInfo.contractBuyMakerDiscount;
        } else {
            fee = isTaker ? fees.contractSellTakerFee : fees.contractSellMakerFee;
            discount = isTaker
                ? userLevelInfo.contractSellTakerDiscount
                : userLevelInfo.contractSellMakerDiscount;
        }

        return Number(fee) * Number(discount);
    };

    const calculateLiquidationPrice = async () => {
        const result = await dispatch(
            performCalculations({
                symbol_id: symbolId,
                open_price: values.priceType === PRICE_TYPE.MARKET ? tokenInfo.c : values.price,
                open_quantity: values.quantity,
                leverage: values.leverage,
                is_long:
                    orderButtonRef.current === CREATE_ORDER_SIDE.BUY
                        ? POSITION_SIDE.LONG
                        : POSITION_SIDE.SHORT,
                calculatorType: CALCULATOR_TYPE.LIQ,
            }),
        ).unwrap();
        setLiquidationPrice(result.liquidationPrice);
    };

    //calculate quantity from amount
    // Q = (Amount * L) / P
    const calculateQuantity = () => {
        const { amount, priceType, price } = updatedValues;

        if (!amount) {
            updatedValues.quantity = '';
            updatedValues.quantityLong = 0;
            updatedValues.quantityShort = 0;
            updatedValues.percentage = 0;
            setValues(updatedValues);
            return;
        }

        let p = 0;
        if (priceType === PRICE_TYPE.MARKET) {
            p = Number(tokenInfo.c);
            const slippage = p * SLIPPAGE;
            p = orderButtonRef.current === CREATE_ORDER_SIDE.SELL ? p - slippage : p + slippage;
        } else {
            p = Number(price);
        }

        const quantityLong = (amount * leverageLong) / p;
        const quantityShort = (amount * leverageShort) / p;

        updatedValues.quantity = roundNumber(
            orderButtonRef.current === CREATE_ORDER_SIDE.BUY ? quantityLong : quantityShort,
            maxTradeQuantityDecimals,
        ).toString();
        updatedValues.quantityLong = roundNumber(quantityLong, maxTradeQuantityDecimals);
        updatedValues.quantityShort = roundNumber(quantityShort, maxTradeQuantityDecimals);
        setValues(updatedValues);
    };

    const calculateValue = () => {
        const { quantityLong, quantityShort } = updatedValues;
        let p = Number(getPrice(updatedValues));
        // reverse contract
        if (isReverse) {
            p = 1 / p;
        }

        // order value
        let orderValueLong: number = p * quantityLong * Number(baseTokenFutures.contractMultiplier);
        let orderValueShort: number =
            p * quantityShort * Number(baseTokenFutures.contractMultiplier);
        orderValueLong = digits(orderValueLong, maxMarginDecimals);
        orderValueShort = digits(orderValueShort, maxMarginDecimals);
        setOrderValue({ long: orderValueLong, short: orderValueShort });
    };

    // Calculate cost and percentage of current inputs
    const calculateCostAndPercentage = (isPercentageOrder?: boolean) => {
        const { quantity, quantityLong, quantityShort, orderSide, orderBy, amount } = updatedValues;
        if (orderSide === ORDER_SIDE.OPEN) {
            let orderShortCost = 0;
            let orderLongCost = 0;
            let p = Number(getPrice(updatedValues));

            // reverse contract
            if (isReverse) {
                p = 1 / p;
            }

            let currentMarginLong = leverageLong ? 1 / leverageLong : 0; // Margin rate under current leverage long
            let currentMarginShort = leverageShort ? 1 / leverageShort : 0; // Margin rate under current leverage short

            orderLongCost =
                currentMarginLong * Number(baseTokenFutures.contractMultiplier) * p * quantityLong;
            orderShortCost =
                currentMarginShort *
                Number(baseTokenFutures.contractMultiplier) *
                p *
                quantityShort;

            orderShortCost = digits(orderShortCost, maxMarginDecimals);
            orderLongCost = digits(orderLongCost, maxMarginDecimals);

            setOrderCost({
                long: orderLongCost,
                short: orderShortCost,
            });

            // open slider percentage
            if (!isPercentageOrder && isAuthenticated && currentAsset) {
                let orderPercentage = 0;
                if (orderBy === ORDER_BY.QUANTITY) {
                    const maxCost =
                        orderLongCost >= orderShortCost ? orderLongCost : orderShortCost;
                    orderPercentage = (maxCost * 100) / Number(currentAsset.availableMargin);
                } else {
                    orderPercentage = (amount * 100) / Number(currentAsset.availableMargin);
                }
                orderPercentage = noNan(orderPercentage);
                orderPercentage = orderPercentage >= 100 ? 100 : orderPercentage;
                orderPercentage = Math.ceil(orderPercentage);
                updatedValues.percentage = orderPercentage;
                setValues(updatedValues);
            }
        } else {
            // close slider percentage
            if (!isPercentageOrder && isAuthenticated) {
                let orderPercentage = 0;

                orderPercentage =
                    (Number(quantity) * 100) /
                    Math.max(
                        Number(symbolTradeableInfo?.longAvailable),
                        Number(symbolTradeableInfo?.shortAvailable),
                    );

                orderPercentage = noNan(orderPercentage);
                orderPercentage = orderPercentage >= 100 ? 100 : orderPercentage;
                orderPercentage = Math.ceil(orderPercentage);
                updatedValues.percentage = orderPercentage;
                setValues(updatedValues);
            }
        }
    };

    const handleChange = (
        value: string | number | number[] | boolean,
        name: string | undefined,
    ) => {
        //don't change when change is not according to Regex or greater than max input
        if (
            (name === 'quantity' && !QuantityRegEx.test(value.toString())) ||
            ((name === 'price' || name === 'triggerPrice') && !PriceRegEx.test(value.toString())) ||
            (name === 'amount' && !AmountRegEx.test(value.toString())) ||
            Number(value) > MAX_INPUT
        ) {
            return;
        }

        // if value is . append 0 before .
        value = value === '.' ? '0.' : value;

        updatedValues = { ...values, [name!]: value };
        updatedErrors = { ...errors, [name!]: undefined };

        if (name === 'orderSide') {
            setIsDualQuantityShow(false);
            updatedValues.orderBy = ORDER_BY.COST;
            updatedValues.amount = 0;
            updatedValues.quantity = '';
            updatedValues.quantityLong = 0;
            updatedValues.quantityShort = 0;
            updatedValues.percentage = 0;
            updatedValues.triggerPrice = 0;
            updatedValues.orderType = ORDER_TYPE.MARKET;
            updatedValues.priceType = PRICE_TYPE.MARKET;
            updatedValues.price = t('market_price');
            updatedValues.postOnly = false;
            updatedValues.timeInForce = TIME_IN_FORCE.GTC;
            updatedErrors = {};
        } else if (name === 'orderType') {
            if (value === ORDER_TYPE.MARKET) {
                updatedValues.price = t('market_price');
                updatedValues.priceType = PRICE_TYPE.MARKET;
            } else {
                updatedValues.price = tokenInfo.c;
                updatedValues.priceType = PRICE_TYPE.LIMIT;
            }
            updatedValues.amount = 0;
            updatedValues.quantity = '';
            updatedValues.quantityLong = 0;
            updatedValues.quantityShort = 0;
            updatedValues.triggerPrice = 0;
            updatedValues.percentage = 0;
            updatedValues.postOnly = false;
            updatedValues.timeInForce = TIME_IN_FORCE.GTC;
            updatedErrors = {};
        } else if (name === 'orderBy') {
            updatedValues.quantity = '';
            updatedValues.quantityLong = 0;
            updatedValues.quantityShort = 0;
            updatedValues.amount = 0;
            updatedValues.percentage = 0;
            updatedErrors = {};
        } else if (name === 'percentage') {
            if (isAuthenticated) {
                if (updatedValues.orderSide === ORDER_SIDE.OPEN && updatedValues.price) {
                    if (currentAsset && currentAsset.availableMargin) {
                        setIsDualQuantityShow(true);
                        updatedValues.amount = roundNumber(
                            (Number(value) * Number(currentAsset.availableMargin)) / 100,
                            maxMarginDecimals,
                        );
                        calculateQuantity();
                    }
                } else if (updatedValues.orderSide === ORDER_SIDE.CLOSE) {
                    if (
                        Number(symbolTradeableInfo?.longAvailable) > 0 ||
                        Number(symbolTradeableInfo?.shortAvailable) > 0
                    ) {
                        updatedValues.quantity = roundNumber(
                            (Math.max(
                                Number(symbolTradeableInfo?.longAvailable),
                                Number(symbolTradeableInfo?.shortAvailable),
                            ) *
                                Number(updatedValues.percentage)) /
                                100,
                            maxTradeQuantityDecimals,
                        ).toString();
                    }
                }
            }
            updatedErrors.quantity = undefined;
            updatedErrors.amount = undefined;
        } else if (name === 'amount') {
            calculateQuantity();
            updatedErrors.quantity = undefined;
        } else if (name === 'postOnly') {
            if (value) {
                updatedValues.timeInForce = TIME_IN_FORCE.GTC;
            }
        } else if (name === 'quantity') {
            updatedValues.quantityLong = Number(updatedValues.quantity);
            updatedValues.quantityShort = Number(updatedValues.quantity);
        }
        calculateValue();
        calculateCostAndPercentage(name === 'percentage');
        setValues(updatedValues);
        setErrors(updatedErrors);
    };

    const preFormSubmit = async () => {
        if (!allowTrading) return;

        if (updatedValues.orderSide === ORDER_SIDE.OPEN) {
            if (orderButtonRef.current === CREATE_ORDER_SIDE.BUY) {
                updatedValues.quantity = updatedValues.quantityLong.toString();
                updatedValues.leverage = leverageLong.toString();
            } else {
                updatedValues.quantity = updatedValues.quantityShort.toString();
                updatedValues.leverage = leverageShort.toString();
            }
            setValues(updatedValues);
        }
        if (values.orderBy === ORDER_BY.COST) {
            calculateQuantity();
        }
        calculateValue();
        calculateCostAndPercentage();

        setOrderConfirmationCheckHigher(false);
        setOrderConfirmationCheckLower(false);
        setOrderConfirmationCheckTrigger(false);
        const priceConfirm = (Number(updatedValues.price) * 100) / Number(tokenInfo.c);

        const result = await validateForm();
        if (isEmptyObj(result)) {
            setTotalFee(
                digits(
                    (orderButtonRef.current === CREATE_ORDER_SIDE.BUY
                        ? orderValue.long
                        : orderValue.short) * calculateFeeRate(),
                    maxMarginDecimals,
                ),
            );
            if (priceConfirm <= 80) {
                await calculateLiquidationPrice();
                setOrderConfirmationCheckLower(true);
                toggleOrderConfirmation();
            } else if (priceConfirm >= 120) {
                await calculateLiquidationPrice();
                setOrderConfirmationCheckHigher(true);
                toggleOrderConfirmation();
            } else if (
                updatedValues.orderType === ORDER_TYPE.CONDITIONAL &&
                Number(updatedValues.price) > Number(updatedValues.triggerPrice)
            ) {
                await calculateLiquidationPrice();
                setOrderConfirmationCheckTrigger(true);
                toggleOrderConfirmation();
            } else if (isConfirm) {
                await calculateLiquidationPrice();
                toggleOrderConfirmation();
            } else {
                handleSubmit();
            }
        }
    };

    // update token info
    useEffect(() => {
        let infoObj = tokenInfoData[symbolId];
        infoObj && setTokenInfo(infoObj);
    }, [tokenInfoData, symbolId]);

    // set last traded price as price value on load
    useEffect(() => {
        if (shouldSetPrice && tokenInfo.c) {
            setFieldValue(
                'price',
                values.priceType === PRICE_TYPE.MARKET ? t('market_price') : tokenInfo.c,
            );
            setShouldSetPrice(false);
        }
    }, [symbolId, tokenInfo]);

    /* reset form on pair change started */
    const resetForm = () => {
        setValues({
            ...initialValues,
            price: values.priceType === PRICE_TYPE.MARKET ? t('market_price') : tokenInfo.c,
            orderType:
                values.orderType === ORDER_TYPE.LIMIT
                    ? ORDER_TYPE.LIMIT
                    : values.orderType === ORDER_TYPE.CONDITIONAL
                    ? ORDER_TYPE.CONDITIONAL
                    : ORDER_TYPE.MARKET,
            priceType: values.priceType === PRICE_TYPE.LIMIT ? PRICE_TYPE.LIMIT : PRICE_TYPE.MARKET,
        });
        setErrors({});
        orderButtonRef.current = '';
        setOrderCost(initialOrderCost);
        setOrderValue({ long: 0, short: 0 });
    };

    useEffect(() => {
        resetForm();
    }, [symbolId]);
    /* reset form on pair change ended */

    useEffect(() => {
        setShouldSetPrice(true);
    }, [currentSymbol]);

    useEffect(() => {
        values.priceType !== PRICE_TYPE.MARKET && setFieldValue('price', createOrderPrice);
    }, [createOrderPrice]);

    useEffect(() => {
        if (leverageLong !== 0 && leverageShort !== 0) {
            calculateQuantity();
            calculateValue();
            calculateCostAndPercentage();
        }
    }, [leverageLong, leverageShort]);

    const changeFutureTradingSetting = (
        isFutureBonusAccount,
        userCurrentFutureTradeAccountIndex,
    ) => {
        const settings = {
            ...commonConfig,
            isFutureBonusAccount: isFutureBonusAccount,
            userCurrentFutureTradeAccountIndex: userCurrentFutureTradeAccountIndex,
        };
        dispatch(setSettings({ common_config: JSON.stringify(settings) }));
        reSubscribeTopic(SOCKET_SUB_TYPE.USER, {
            [SOCKET_TOPICS.FUTURES_ORDER]: {
                id: 'futures_order',
                topic: 'futures_order',
                event: 'sub',
                accountIndex: userCurrentFutureTradeAccountIndex,
                params: {
                    org: Number(currentSymbol.orgId),
                    binary: true,
                },
            },
            [SOCKET_TOPICS.FUTURES_POSITION]: {
                id: 'futures_position',
                topic: 'futures_position',
                event: 'sub',
                accountIndex: userCurrentFutureTradeAccountIndex,
                params: {
                    org: Number(currentSymbol.orgId),
                    binary: true,
                },
            },
            [SOCKET_TOPICS.FUTURES_TRADEABLE]: {
                id: 'futures_tradeable',
                topic: 'futures_tradeable',
                event: 'sub',
                accountIndex: userCurrentFutureTradeAccountIndex,
                params: { binary: false },
            },
        });
    };

    useEffect(() => {
        if (commonConfig !== undefined && !isEmptyObj(commonConfig)) {
            const { isFutureBonusAccount, userCurrentFutureTradeAccountIndex } = commonConfig ?? {};
            if (isFutureBonusAccount) {
                const index = userCurrentFutureTradeAccountIndex ?? DEFAULT_ACCOUNT_INDEX;
                dispatch(changeFutureTradingAccount(index));
            } else {
                dispatch(changeFutureTradingAccount(DEFAULT_ACCOUNT_INDEX));
            }
        } else {
            dispatch(changeFutureTradingAccount(DEFAULT_ACCOUNT_INDEX));
        }
    }, [commonConfig]);

    useEffect(() => {
        if (
            isFutureBonusAccount &&
            userBonus?.status === BonusStatusEnum.EXPIRED &&
            userBonus?.type === BonusTypeEnum.FUTURES_ACCOUNT
        ) {
            changeFutureTradingSetting(false, DEFAULT_ACCOUNT_INDEX);
        }
    }, [userBonus, isFutureBonusAccount]);

    //TODO:: We will remove this condition later, for this this condition is for existing users who using futures bonus fund
    useEffect(() => {
        if (
            userCurrentFutureTradeAccountIndex !== undefined &&
            userCurrentFutureTradeAccountIndex !== DEFAULT_ACCOUNT_INDEX &&
            !getLeverageLoading &&
            (leverageLong > 5 || leverageShort > 5)
        ) {
            if (leverageLong > 5) {
                dispatch(
                    setLeverage({
                        symbol_id: symbolId,
                        leverage: '5',
                        is_long: Number(POSITION_SIDE.LONG),
                        account_index: userCurrentFutureTradeAccountIndex,
                    }),
                );
            }
            if (leverageShort > 5) {
                dispatch(
                    setLeverage({
                        symbol_id: symbolId,
                        leverage: '5',
                        is_long: Number(POSITION_SIDE.SHORT),
                        account_index: userCurrentFutureTradeAccountIndex,
                    }),
                );
            }
        }
    }, [getLeverageLoading, leverageLong, leverageShort]);

    useEffect(() => {
        if (
            !isEmptyObj(userVerifyInfo) &&
            userVerifyInfo?.verifyStatus !== UserVerificationStatusEnum.VERIFIED &&
            userCurrentFutureTradeAccountIndex !== undefined &&
            userCurrentFutureTradeAccountIndex !== DEFAULT_ACCOUNT_INDEX
        ) {
            changeFutureTradingSetting(false, DEFAULT_ACCOUNT_INDEX);
            setIsShowKYCModalOpen(true);
        }
    }, [userVerifyInfo, userCurrentFutureTradeAccountIndex]);

    const setFutureTradingAccount = () => {
        if (
            !isEmptyObj(userVerifyInfo) &&
            userVerifyInfo?.verifyStatus !== UserVerificationStatusEnum.VERIFIED
        ) {
            setIsShowKYCModalOpen(true);
            return;
        }
        const index = !isFutureBonusAccount
            ? userWallets.find((wallet) => wallet.isBonusAccount)?.accountIndex
            : DEFAULT_ACCOUNT_INDEX;
        changeFutureTradingSetting(!isFutureBonusAccount, index);
    };

    return (
        <>
            <StyledForm>
                <Tabs
                    disabled={!allowTrading}
                    className="order-side"
                    name="orderSide"
                    styles={{ borderBottom: `1px solid ${colors.border.light}` }}
                    value={values.orderSide}
                    onChange={handleChange}
                >
                    {Object.keys(ORDER_SIDE).map((tab, index) => {
                        return (
                            <Tab
                                value={ORDER_SIDE[tab as keyof typeof ORDER_SIDE]}
                                key={index}
                                color={
                                    ORDER_SIDE[tab as keyof typeof ORDER_SIDE] === ORDER_SIDE.OPEN
                                        ? colors.alert.green
                                        : colors.alert.red
                                }
                                label={t(tab.toLowerCase())}
                                className={
                                    ORDER_SIDE[tab as keyof typeof ORDER_SIDE] === ORDER_SIDE.OPEN
                                        ? 'OrderSide'
                                        : ''
                                }
                            />
                        );
                    })}
                </Tabs>
                {userBonus?.type === BonusTypeEnum.FUTURES_ACCOUNT &&
                    userBonus?.status === BonusStatusEnum.ACTIVE && (
                        <FlexContainer
                            styles={{
                                justifyContent: 'space-between',
                                backgroundColor: colors.background.secondary.v4,
                                height: 39,
                                alignItems: 'center',
                                padding: '0 12px',
                            }}
                        >
                            <FlexContainer styles={{ gap: '8px' }}>
                                <StyledLabel color={colors.text.primary}>
                                    {t('futures_bonus_use', {
                                        bonus: currencyFormatter(Number(userBonus?.amountUSDT), 0),
                                    })}
                                </StyledLabel>
                                <Info
                                    className="clickable"
                                    fill={colors.text.tertiary}
                                    size={14}
                                    onClick={() => setIsFuturesFundTradeInstructionModalOpen(true)}
                                />
                            </FlexContainer>
                            <Switch
                                disabled={!allowTrading}
                                value={isFutureBonusAccount}
                                name="change_futures_account"
                                onChange={() => setFutureTradingAccount()}
                            />
                        </FlexContainer>
                    )}
                <div className="form-elements">
                    <LeverageContainer>
                        <LeverageTypeLabel>{t('leverage_isolated')}</LeverageTypeLabel>
                        <FlexContainer
                            disabled={!allowTrading}
                            styles={{
                                justifyContent: 'space-between',
                                width: '100%',
                                padding: '12px 10px',
                                cursor: !allowTrading ? 'not-allowed' : 'pointer',
                            }}
                            onClick={() =>
                                !allowTrading ? undefined : setIsLeverageModalOpen(true)
                            }
                        >
                            <FlexContainer styles={{ gap: 10 }}>
                                <LeverageValueText color={colors.exchange.buy}>
                                    {`${leverageLong}x`}
                                </LeverageValueText>
                                <LeverageValueText color={colors.exchange.sell}>
                                    {`${leverageShort}x`}
                                </LeverageValueText>
                            </FlexContainer>
                            <DropDownArrow stroke={colors.text.primary} />
                        </FlexContainer>
                    </LeverageContainer>
                    <Tabs
                        disabled={!allowTrading}
                        className="order-type"
                        name="orderType"
                        value={values.orderType}
                        onChange={handleChange}
                    >
                        {Object.keys(ORDER_TYPE).map((tab, index) => {
                            return (
                                <Tab
                                    value={ORDER_TYPE[tab as keyof typeof ORDER_TYPE]}
                                    key={index}
                                    label={t(tab.toLowerCase())}
                                    isButton={true}
                                />
                            );
                        })}
                    </Tabs>
                    {values.orderType === ORDER_TYPE.CONDITIONAL && (
                        <Input
                            disabled={!allowTrading}
                            name="triggerPrice"
                            label={t('trigger_price')}
                            value={values.triggerPrice}
                            error={errors['triggerPrice']}
                            onChange={!allowTrading ? undefined : handleChange}
                            onBlur={handleBlur}
                        />
                    )}
                    <Input
                        name="price"
                        label={t('order_price')}
                        value={values.price}
                        disabled={values.priceType === PRICE_TYPE.MARKET || !allowTrading}
                        containerStyles={{ paddingRight: 0 }}
                        error={errors['price']}
                        onChange={!allowTrading ? undefined : handleChange}
                        onBlur={handleBlur}
                        rightElement={
                            values.priceType !== PRICE_TYPE.MARKET && (
                                <Tooltip overlay={t('fill_in_the_last_traded_price')}>
                                    <Button
                                        disabled={!allowTrading}
                                        onClick={() =>
                                            !allowTrading
                                                ? undefined
                                                : setFieldValue('price', tokenInfo.c)
                                        }
                                        variant="text"
                                        type="button"
                                        color={colors.primary}
                                        size="small"
                                    >
                                        {t('last')}
                                    </Button>
                                </Tooltip>
                            )
                        }
                    />
                    <div>
                        <Input
                            disabled={!allowTrading}
                            name={values.orderBy === ORDER_BY.QUANTITY ? 'quantity' : 'amount'}
                            label={
                                values.orderBy === ORDER_BY.QUANTITY ? t('quantity') : t('amount')
                            }
                            containerStyles={{ paddingRight: 0, marginBottom: '2px !important' }}
                            value={
                                values.orderBy === ORDER_BY.QUANTITY
                                    ? values.quantity
                                    : values.amount
                            }
                            error={
                                errors[values.orderBy === ORDER_BY.QUANTITY ? 'quantity' : 'amount']
                            }
                            onChange={!allowTrading ? undefined : handleChange}
                            onBlur={handleBlur}
                            fullWidth
                            hasInput={!isDualQuantityShow}
                            rightElement={
                                values.orderSide === ORDER_SIDE.OPEN ? (
                                    <>
                                        {isDualQuantityShow && (
                                            <FlexContainer
                                                styles={{
                                                    width: '100%',
                                                    justifyContent: 'flex-end',
                                                }}
                                            >
                                                <QuantityContainer onClick={hideDualQuantity}>
                                                    <ValueText className="up">
                                                        {values.orderBy === ORDER_BY.QUANTITY
                                                            ? updatedValues.quantityLong
                                                            : orderCost.long}
                                                    </ValueText>
                                                    <Separator>/</Separator>
                                                    <ValueText className="down">
                                                        {values.orderBy === ORDER_BY.QUANTITY
                                                            ? updatedValues.quantityShort
                                                            : orderCost.short}
                                                    </ValueText>
                                                </QuantityContainer>
                                            </FlexContainer>
                                        )}
                                        <Select
                                            disabled={!allowTrading}
                                            name="orderBy"
                                            headStyles={{ border: 'none' }}
                                            value={values.orderBy}
                                            onChange={handleChange}
                                        >
                                            <SelectItem value={ORDER_BY.QUANTITY}>
                                                {secondLevelUnderlyingName}
                                            </SelectItem>
                                            <SelectItem value={ORDER_BY.COST}>
                                                {quoteTokenName}
                                            </SelectItem>
                                        </Select>
                                    </>
                                ) : (
                                    <InputRightLabel>{secondLevelUnderlyingName}</InputRightLabel>
                                )
                            }
                        />
                        {values.orderBy === ORDER_BY.QUANTITY &&
                            values.priceType === PRICE_TYPE.LIMIT &&
                            values.orderSide === ORDER_SIDE.OPEN && (
                                <FlexContainer className="cost-container">
                                    <LabelText>{t('margin')}</LabelText>
                                    <div className="value-container">
                                        <ValueText className="up">{orderCost.long}</ValueText>
                                        <Separator>/</Separator>
                                        <ValueText className="down">{orderCost.short}</ValueText>
                                        &nbsp;
                                        <ValueText>{quoteTokenName}</ValueText>
                                    </div>
                                </FlexContainer>
                            )}
                        {values.orderBy === ORDER_BY.COST &&
                            values.priceType === PRICE_TYPE.LIMIT &&
                            values.orderSide === ORDER_SIDE.OPEN && (
                                <FlexContainer className="cost-container">
                                    <LabelText>{t('qty')}</LabelText>
                                    <div className="value-container">
                                        <ValueText className="up">
                                            {updatedValues.quantityLong}
                                        </ValueText>
                                        <Separator>/</Separator>
                                        <ValueText className="down">
                                            {updatedValues.quantityShort}
                                        </ValueText>
                                        &nbsp;
                                        <ValueText>{secondLevelUnderlyingName}</ValueText>
                                    </div>
                                </FlexContainer>
                            )}
                    </div>
                    <Range
                        disabled={!allowTrading}
                        name="percentage"
                        value={values.percentage}
                        onChange={!allowTrading ? undefined : handleChange}
                        // disabled
                    />
                    {isMobile && (
                        <FlexContainer styles={{ justifyContent: 'space-between' }}>
                            <Checkbox
                                name="postOnly"
                                value={values.postOnly}
                                label={t('post_only')}
                                onChange={!allowTrading ? undefined : handleChange}
                            />
                            <FlexContainer>
                                <Select
                                    name="timeInForce"
                                    styles={{ width: 59, marginRight: 8 }}
                                    headStyles={{ padding: '0px 8px' }}
                                    value={values.timeInForce}
                                    disabled={values.postOnly || !allowTrading}
                                    onChange={!allowTrading ? undefined : handleChange}
                                >
                                    {Object.keys(TIME_IN_FORCE).map((key) => {
                                        return (
                                            TIME_IN_FORCE[key as keyof typeof TIME_IN_FORCE] !==
                                                TIME_IN_FORCE.MAKER && (
                                                <SelectItem
                                                    value={
                                                        TIME_IN_FORCE[
                                                            key as keyof typeof TIME_IN_FORCE
                                                        ]
                                                    }
                                                    key={key}
                                                >
                                                    {key}
                                                </SelectItem>
                                            )
                                        );
                                    })}
                                </Select>
                                <Tooltip overlay={t(values.timeInForce.toLowerCase())}>
                                    {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                                    <a>
                                        <Question
                                            fill={colors.brand.main}
                                            stroke={colors.primary}
                                        />
                                    </a>
                                </Tooltip>
                            </FlexContainer>
                        </FlexContainer>
                    )}
                    {isAuthenticated && isMobile && (
                        <FlexContainer className="info-container">
                            <LabelText>{t('available_balance')}</LabelText>
                            <ValueText>
                                {currentAsset.availableMargin
                                    ? roundNumber(
                                          Number(currentAsset.availableMargin),
                                          maxMarginDecimals,
                                      )
                                    : '--'}{' '}
                                {currentAsset.tokenName}
                            </ValueText>
                        </FlexContainer>
                    )}
                    {values.orderSide === ORDER_SIDE.CLOSE && (
                        <FlexContainer className="info-container">
                            <FlexContainer className="order-value" styles={{ marginRight: 10 }}>
                                <LabelText>{t('close_short')}</LabelText>
                                <ValueText>
                                    {symbolTradeableInfo && symbolTradeableInfo?.shortAvailable
                                        ? Number(symbolTradeableInfo?.shortAvailable)
                                        : '0'}{' '}
                                    {secondLevelUnderlyingName}
                                </ValueText>
                            </FlexContainer>
                            <FlexContainer
                                className="order-value"
                                styles={{ textAlign: 'right', marginLeft: 10 }}
                            >
                                <LabelText>{t('close_long')}</LabelText>
                                <ValueText>
                                    {symbolTradeableInfo && symbolTradeableInfo?.longAvailable
                                        ? Number(symbolTradeableInfo?.longAvailable)
                                        : '0'}{' '}
                                    {secondLevelUnderlyingName}
                                </ValueText>
                            </FlexContainer>
                        </FlexContainer>
                    )}
                    {values.orderSide === ORDER_SIDE.OPEN &&
                        values.priceType === PRICE_TYPE.LIMIT && (
                            <FlexContainer className="info-container">
                                <FlexContainer className="order-value">
                                    <LabelText>{t('order_value')}</LabelText>
                                    <ValueText>
                                        {orderValue.long} {quoteTokenName}
                                    </ValueText>
                                </FlexContainer>
                                <FlexContainer
                                    className="order-value"
                                    styles={{ textAlign: 'right' }}
                                >
                                    <LabelText>{t('order_value')}</LabelText>
                                    <ValueText>
                                        {orderValue.short} {quoteTokenName}
                                    </ValueText>
                                </FlexContainer>
                            </FlexContainer>
                        )}
                    {isAuthenticated ? (
                        <FlexContainer className="buttons-container">
                            <Button
                                type="button"
                                fullWidth
                                disabled={createOrderLoading || !allowTrading}
                                loading={
                                    orderButtonRef.current === CREATE_ORDER_SIDE.BUY &&
                                    createOrderLoading
                                }
                                className="bg-up left-button OrderBtn"
                                onClick={() => {
                                    orderButtonRef.current = CREATE_ORDER_SIDE.BUY;
                                    preFormSubmit();
                                }}
                            >
                                {t(
                                    values.orderSide === ORDER_SIDE.OPEN
                                        ? 'open_long'
                                        : 'close_short',
                                )}
                            </Button>
                            <Button
                                type="button"
                                fullWidth
                                disabled={createOrderLoading || !allowTrading}
                                loading={
                                    orderButtonRef.current === CREATE_ORDER_SIDE.SELL &&
                                    createOrderLoading
                                }
                                className="bg-down right-button"
                                onClick={() => {
                                    orderButtonRef.current = CREATE_ORDER_SIDE.SELL;
                                    preFormSubmit();
                                }}
                            >
                                {t(
                                    values.orderSide === ORDER_SIDE.OPEN
                                        ? 'open_short'
                                        : 'close_long',
                                )}
                            </Button>
                        </FlexContainer>
                    ) : (
                        <FlexContainer className="buttons-container">
                            <Button
                                styles={{
                                    color: `${colors.primary} !important`,
                                }}
                                type="button"
                                className="left-button"
                                fullWidth
                                variant="outlined"
                                onClick={() => setCurrentUrl(APP_URLS.LOGIN)}
                            >
                                {t('login')}
                            </Button>
                            <Button
                                className="right-button"
                                type="button"
                                fullWidth
                                styles={{
                                    color: `${colors.brand.secondary} !important`,
                                }}
                                onClick={() => setCurrentUrl(APP_URLS.REGISTER)}
                            >
                                {t('signup')}
                            </Button>
                        </FlexContainer>
                    )}
                    {!isMobile && values.priceType !== PRICE_TYPE.MARKET && (
                        <FlexContainer styles={{ justifyContent: 'space-between' }}>
                            <Checkbox
                                name="postOnly"
                                value={values.postOnly}
                                label={t('post_only')}
                                onChange={!allowTrading ? undefined : handleChange}
                            />
                            <FlexContainer>
                                <Select
                                    name="timeInForce"
                                    styles={{
                                        width: 59,
                                        marginRight: 8,
                                    }}
                                    headStyles={{ padding: '0px 8px' }}
                                    value={values.timeInForce}
                                    disabled={values.postOnly || !allowTrading}
                                    onChange={!allowTrading ? undefined : handleChange}
                                >
                                    {Object.keys(TIME_IN_FORCE).map((key) => {
                                        return (
                                            TIME_IN_FORCE[key as keyof typeof TIME_IN_FORCE] !==
                                                TIME_IN_FORCE.MAKER && (
                                                <SelectItem
                                                    value={
                                                        TIME_IN_FORCE[
                                                            key as keyof typeof TIME_IN_FORCE
                                                        ]
                                                    }
                                                    key={key}
                                                >
                                                    {key}
                                                </SelectItem>
                                            )
                                        );
                                    })}
                                </Select>
                                <Tooltip overlay={t(values.timeInForce.toLowerCase())}>
                                    {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                                    <a>
                                        <Question
                                            fill={colors.brand.main}
                                            stroke={colors.primary}
                                        />
                                    </a>
                                </Tooltip>
                            </FlexContainer>
                        </FlexContainer>
                    )}
                </div>
            </StyledForm>
            {isOrderConfirmationOpen && (
                <ConfirmationModal
                    handleClose={toggleOrderConfirmation}
                    handleSubmit={handleSubmit}
                    orderCost={
                        orderButtonRef.current === CREATE_ORDER_SIDE.BUY
                            ? orderCost.long
                            : orderCost.short
                    }
                    orderValue={
                        orderButtonRef.current === CREATE_ORDER_SIDE.BUY
                            ? orderValue.long
                            : orderValue.short
                    }
                    orderFee={totalFee}
                    liquidationPrice={liquidationPrice}
                    currentSymbol={currentSymbol}
                    values={values}
                    isOpen={isOrderConfirmationOpen}
                    higherPriceCheck={orderConfirmationCheckHigher}
                    lowerPriceCheck={orderConfirmationCheckLower}
                    triggerPriceCheck={orderConfirmationCheckTrigger}
                    orderButtonRef={orderButtonRef}
                />
            )}
            {isLeverageModalOpen && (
                <LeverageModal
                    open={isLeverageModalOpen}
                    handleClose={() => {
                        changeScrollBehavior(false);
                        setIsLeverageModalOpen(false);
                    }}
                />
            )}
            {isFuturesFundTradeInstructionModalOpen && (
                <FuturesFundTradeInstructionModal
                    open={isFuturesFundTradeInstructionModalOpen}
                    handleClose={() => {
                        setIsFuturesFundTradeInstructionModalOpen(false);
                    }}
                />
            )}
            {isShowKYCModalOpen && (
                <CompleteKYCModal
                    open={isShowKYCModalOpen}
                    handleClose={() => {
                        setIsShowKYCModalOpen(false);
                    }}
                />
            )}
        </>
    );
};

export { Form };
